import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Output, ViewChild, ContentChild, forwardRef, Directive, NgModule } from '@angular/core';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { Subject } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BaseDirective } from '@eui/components/shared';
import * as i1 from '@eui/components/eui-dialog';
import { EuiDialogService } from '@eui/components/eui-dialog';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { EuiButtonModule } from '@eui/components/eui-button';
import { EuiIconModule } from '@eui/components/eui-icon';

class EuiMessageBoxService {
    constructor(euiDialogService) {
        this.euiDialogService = euiDialogService;
    }
    /**
     * Open a Message box.
     *
     * @param { EuiDialogInterface } config Configuration applied to the Message box.
     */
    openMessageBox(config = {}) {
        this.euiDialogService.openDialog({
            ...config,
            header: null,
            headerComponent: null,
            isClosedOnClickOutside: false,
            hasCloseButton: false,
            isClosedOnEscape: false,
            hasFooter: true,
        });
    }
    /**
     * Close a Message box.
     */
    closeMessageBox() {
        this.euiDialogService.closeDialog();
    }
}
/** @nocollapse */ EuiMessageBoxService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxService, deps: [{ token: i1.EuiDialogService }], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ EuiMessageBoxService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.EuiDialogService }]; } });

class EuiMessageBoxComponent extends BaseDirective {
    constructor(viewContainerRef, euiMessageBoxService) {
        super();
        this.viewContainerRef = viewContainerRef;
        this.euiMessageBoxService = euiMessageBoxService;
        /** @deprecated it will be removed in next eUI version */
        this.e2eAttr = 'eui-dialog';
        this.width = '33rem';
        this.height = 'auto';
        this._hasAcceptButton = true;
        this._hasDismissButton = true;
        this.acceptLabel = 'eui.OK';
        this.dismissLabel = 'eui.CANCEL';
        this.typeClass = 'primary';
        this._isHandleCloseOnDismiss = false;
        this._isHandleCloseOnClose = false;
        this._isHandleCloseOnAccept = false;
        this._isDraggable = false;
        this._hasNoBodyPadding = false;
        this.open = new EventEmitter();
        this.close = new EventEmitter();
        this.dismiss = new EventEmitter();
        this.accept = new EventEmitter();
        this.destroy$ = new Subject();
    }
    get hasAcceptButton() { return this._hasAcceptButton; }
    set hasAcceptButton(value) { this._hasAcceptButton = coerceBooleanProperty(value); }
    get hasDismissButton() { return this._hasDismissButton; }
    set hasDismissButton(value) { this._hasDismissButton = coerceBooleanProperty(value); }
    get isHandleCloseOnDismiss() { return this._isHandleCloseOnDismiss; }
    set isHandleCloseOnDismiss(value) { this._isHandleCloseOnDismiss = coerceBooleanProperty(value); }
    get isHandleCloseOnClose() { return this._isHandleCloseOnClose; }
    set isHandleCloseOnClose(value) { this._isHandleCloseOnClose = coerceBooleanProperty(value); }
    get isHandleCloseOnAccept() { return this._isHandleCloseOnAccept; }
    set isHandleCloseOnAccept(value) { this._isHandleCloseOnAccept = coerceBooleanProperty(value); }
    get isDraggable() { return this._isDraggable; }
    set isDraggable(value) { this._isDraggable = coerceBooleanProperty(value); }
    get hasNoBodyPadding() { return this._hasNoBodyPadding; }
    set hasNoBodyPadding(value) { this._hasNoBodyPadding = coerceBooleanProperty(value); }
    ngOnInit() {
        if (this.euiPrimary) {
            this.typeClass = 'primary';
        }
        if (this.euiSecondary) {
            this.typeClass = 'secondary';
        }
        if (this.euiInfo) {
            this.typeClass = 'info';
        }
        if (this.euiSuccess) {
            this.typeClass = 'success';
        }
        if (this.euiWarning) {
            this.typeClass = 'warning';
        }
        if (this.euiDanger) {
            this.typeClass = 'danger';
        }
        if (this.euiAccent) {
            this.typeClass = 'accent';
        }
    }
    ngAfterViewInit() {
        this.templatePortalContent = new TemplatePortal(this.templateRefContent, this.viewContainerRef);
        if (this.euiMessageBoxFooterDirective) {
            this.templatePortalFooter = new TemplatePortal(this.templateRefFooter, this.viewContainerRef);
        }
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    /**
     * Open a Message Box.
     */
    openMessageBox() {
        const config = {
            e2eAttr: this.e2eAttr,
            title: this.title,
            width: this.width,
            height: this.height,
            typeClass: this.typeClass,
            acceptLabel: this.acceptLabel,
            dismissLabel: this.dismissLabel,
            hasCloseButton: false,
            hasAcceptButton: this.hasAcceptButton,
            hasDismissButton: this.hasDismissButton,
            isClosedOnClickOutside: false,
            isClosedOnEscape: false,
            isHandleCloseOnDismiss: this.isHandleCloseOnDismiss,
            isHandleCloseOnClose: this.isHandleCloseOnClose,
            isHandleCloseOnAccept: this.isHandleCloseOnAccept,
            isHandleCloseOnClickOutside: false,
            isHandleCloseOnEscape: false,
            isDraggable: coerceBooleanProperty(this.isDraggable),
            hasNoBodyPadding: coerceBooleanProperty(this.hasNoBodyPadding),
            content: this.templatePortalContent,
            footer: this.templatePortalFooter,
            isMessageBox: true,
            open: () => {
                this.open.emit();
            },
            close: () => {
                this.close.emit();
            },
            dismiss: () => {
                this.dismiss.emit();
            },
            accept: () => {
                this.accept.emit();
            },
        };
        this.euiMessageBoxService.openMessageBox(config);
    }
    /**
     * Close a Message Box.
     */
    closeMessageBox() {
        this.euiMessageBoxService.closeMessageBox();
    }
}
/** @nocollapse */ EuiMessageBoxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxComponent, deps: [{ token: i0.ViewContainerRef }, { token: EuiMessageBoxService }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiMessageBoxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiMessageBoxComponent, selector: "eui-message-box", inputs: { e2eAttr: "e2eAttr", title: "title", width: "width", height: "height", hasAcceptButton: "hasAcceptButton", hasDismissButton: "hasDismissButton", acceptLabel: "acceptLabel", dismissLabel: "dismissLabel", typeClass: "typeClass", isHandleCloseOnDismiss: "isHandleCloseOnDismiss", isHandleCloseOnClose: "isHandleCloseOnClose", isHandleCloseOnAccept: "isHandleCloseOnAccept", isDraggable: "isDraggable", hasNoBodyPadding: "hasNoBodyPadding" }, outputs: { open: "open", close: "close", dismiss: "dismiss", accept: "accept" }, queries: [{ propertyName: "euiMessageBoxFooterDirective", first: true, predicate: i0.forwardRef(function () { return EuiMessageBoxFooterDirective; }), descendants: true }], viewQueries: [{ propertyName: "templateRefContent", first: true, predicate: ["templateRefContent"], descendants: true }, { propertyName: "templateRefFooter", first: true, predicate: ["templateRefFooter"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<ng-template #templateRefContent>\n    <ng-content></ng-content>\n</ng-template>\n<ng-template #templateRefFooter>\n    <ng-content select=\"eui-message-box-footer\"></ng-content>\n</ng-template>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-message-box', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ng-template #templateRefContent>\n    <ng-content></ng-content>\n</ng-template>\n<ng-template #templateRefFooter>\n    <ng-content select=\"eui-message-box-footer\"></ng-content>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }, { type: EuiMessageBoxService }]; }, propDecorators: { e2eAttr: [{
                type: Input
            }], title: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], hasAcceptButton: [{
                type: Input
            }], hasDismissButton: [{
                type: Input
            }], acceptLabel: [{
                type: Input
            }], dismissLabel: [{
                type: Input
            }], typeClass: [{
                type: Input
            }], isHandleCloseOnDismiss: [{
                type: Input
            }], isHandleCloseOnClose: [{
                type: Input
            }], isHandleCloseOnAccept: [{
                type: Input
            }], isDraggable: [{
                type: Input
            }], hasNoBodyPadding: [{
                type: Input
            }], open: [{
                type: Output
            }], close: [{
                type: Output
            }], dismiss: [{
                type: Output
            }], accept: [{
                type: Output
            }], templateRefContent: [{
                type: ViewChild,
                args: ['templateRefContent']
            }], templateRefFooter: [{
                type: ViewChild,
                args: ['templateRefFooter']
            }], euiMessageBoxFooterDirective: [{
                type: ContentChild,
                args: [forwardRef(() => EuiMessageBoxFooterDirective)]
            }] } });
/* eslint-disable */
class EuiMessageBoxFooterDirective {
}
/** @nocollapse */ EuiMessageBoxFooterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxFooterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ EuiMessageBoxFooterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: EuiMessageBoxFooterDirective, selector: "eui-message-box-footer", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxFooterDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'eui-message-box-footer' }]
        }] });

class EuiMessageBoxConfig {
    constructor(values) {
        this.e2eAttr = 'eui-message-box';
        this.acceptLabel = 'eui.OK';
        this.dismissLabel = 'eui.CANCEL';
        this.typeClass = 'primary';
        this.width = '33rem';
        this.height = 'auto';
        this.hasCloseButton = false;
        this.hasAcceptButton = true;
        this.hasDismissButton = true;
        this.isClosedOnClickOutside = false;
        this.isClosedOnEscape = false;
        this.isHandleCloseOnDismiss = false;
        this.isHandleCloseOnClose = false;
        this.isHandleCloseOnAccept = false;
        this.isHandleCloseOnClickOutside = false;
        this.isHandleCloseOnEscape = false;
        this.hasFooter = true;
        this.isMessageBox = true;
        this.header = null;
        this.headerComponent = null;
        Object.assign(this, values);
    }
}
class EuiMessageBoxComponentInstances {
    constructor() {
        this.headerComponent = null;
        this.bodyComponent = null;
        this.footerComponent = null;
    }
}

class EuiMessageBoxModule {
}
/** @nocollapse */ EuiMessageBoxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiMessageBoxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxModule, declarations: [EuiMessageBoxComponent,
        EuiMessageBoxFooterDirective], imports: [CommonModule,
        EuiButtonModule,
        EuiIconModule,
        TranslateModule,
        PortalModule,
        OverlayModule,
        A11yModule,
        DragDropModule], exports: [EuiMessageBoxComponent,
        EuiMessageBoxFooterDirective] });
/** @nocollapse */ EuiMessageBoxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxModule, providers: [
        EuiMessageBoxService,
        EuiDialogService,
    ], imports: [CommonModule,
        EuiButtonModule,
        EuiIconModule,
        TranslateModule,
        PortalModule,
        OverlayModule,
        A11yModule,
        DragDropModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiMessageBoxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        EuiButtonModule,
                        EuiIconModule,
                        TranslateModule,
                        PortalModule,
                        OverlayModule,
                        A11yModule,
                        DragDropModule,
                    ],
                    exports: [
                        EuiMessageBoxComponent,
                        EuiMessageBoxFooterDirective,
                    ],
                    declarations: [
                        EuiMessageBoxComponent,
                        EuiMessageBoxFooterDirective,
                    ],
                    providers: [
                        EuiMessageBoxService,
                        EuiDialogService,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiMessageBoxComponent, EuiMessageBoxComponentInstances, EuiMessageBoxConfig, EuiMessageBoxFooterDirective, EuiMessageBoxModule, EuiMessageBoxService };
